/**
 * trialTodoAdapter — le bandeau d'essai de l'accueil de l'élève : « compléter son profil » avant
 * sa séance d'essai, en ligne « à faire » (`<TodoRow>`).
 *
 * Port littéral de `showCompleteProfileBanner` et de `goToStep` (`TodoListScreen.js:222-251`,
 * `:616-622`), pour l'élève lui-même hors live (l'espace élève n'existe qu'ainsi) :
 *  - étape 1 : l'appel du parent n'est pas encore demandé (`profile.parent_call_requested === null`)
 *    → `trial-school-subjects` ;
 *  - étape 2 : aucun XP et aucune activité « demande pour la séance » (à faire ou en cours)
 *    → `trial-preparation-form` ;
 *  - seulement en essai générique, pas abonné, à 7 jours au plus de la fin de l'essai ;
 *  - jamais le jour du live, sauf quand il est passé.
 *
 * Les mots sont ceux de la maquette (`studentTrialTodoMock`), tutoyés : le legacy écrivait
 * « Complétez les informations de {prénom} » sur l'écran de l'élève lui-même.
 *
 * Écart voulu : sans live du tout, le legacy comparait « maintenant » à lui-même (`moment(undefined)`)
 * et montrait ou non le bandeau selon la milliseconde ; ici, pas de live = pas le jour du live.
 */
import moment from "moment"

import { isSubscribed } from "../../parent/Subscription/subscriptionAdapter"
import { type UITodoRowItem } from "../../../ui/types/studentHome"

type ApiActivityTitle = { material?: { title?: string | null } | null }

export type ApiTrialAccount = {
    roles?: string[]
    xp?: number
    trial_ends_at?: string | null
    subscription_state?: { status?: string | null } | null
    profile?: { parent_call_requested?: unknown } | null
    todo_activities?: ApiActivityTitle[] | null
    activities?: ApiActivityTitle[] | null
    live_sessions?: { next_occurrences?: { date?: string; finalized_at?: string | null }[] | null }[] | null
}

const TRIAL_ROUTES = {
    schoolSubjects: "trial-school-subjects",
    preparationForm: "trial-preparation-form",
} as const

const TEXTS = {
    label: "Complète tes informations",
    detail: "Pour préparer au mieux la séance d'essai",
    cta: "Compléter",
}

const SESSION_REQUEST = "demande pour la séance"

const hasSessionRequest = (activities: ApiActivityTitle[] | null | undefined) =>
    (activities ?? []).some((activity) => activity?.material?.title?.toLowerCase().includes(SESSION_REQUEST))

/**
 * La prochaine occurrence non finalisée, passée comprise (`findClosestNextOccurrence`,
 * `LiveRoomCard.js:13-33`) — pas celle du bandeau du live, qui écarte les jours passés.
 */
function closestNextOccurrenceDate(account: ApiTrialAccount): string | null {
    const dates = (account.live_sessions ?? [])
        .flatMap((session) => session?.next_occurrences ?? [])
        .filter((occurrence) => !occurrence?.finalized_at && occurrence?.date)
        .map((occurrence) => occurrence.date as string)
        .sort((a, b) => moment(a).diff(b))
    return dates[0] ?? null
}

/** La ligne du bandeau, ou `null` quand le legacy ne le montrait pas. `now` : pour les tests. */
export function toStudentTrialTodo(
    account: ApiTrialAccount | null | undefined,
    now: moment.Moment = moment()
): UITodoRowItem | null {
    if (account == null || !(account.roles ?? []).includes("student")) return null

    const step1 = account.profile?.parent_call_requested === null
    const step2 =
        account.xp === 0 && !hasSessionRequest(account.todo_activities) && !hasSessionRequest(account.activities)
    if (!step1 && !step2) return null

    if (isSubscribed(account)) return null
    if (account.subscription_state?.status !== "on_generic_trial") return null
    if (Math.abs(now.diff(moment(account.trial_ends_at), "days")) > 7) return null

    const liveDate = closestNextOccurrenceDate(account)
    if (liveDate != null) {
        const live = moment(liveDate)
        const isToday = live.isSame(now, "day")
        const isPast = live.isBefore(now)
        if (isToday && !isPast) return null
    }

    return {
        id: "trial-profile",
        kind: "trial_profile",
        label: TEXTS.label,
        detail: TEXTS.detail,
        cta: TEXTS.cta,
        route: step1 ? TRIAL_ROUTES.schoolSubjects : TRIAL_ROUTES.preparationForm,
    }
}
